import { useEffect, useMemo, useRef, useState } from "react";

import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";

function parentIdFor(edges: WorkspaceEdge[], nodeId: string): string | null {
  return edges.find((edge) => edge.targetNodeId === nodeId)?.sourceNodeId ?? null;
}

/** Seed map from current report parents on the canvas (no API). */
export function simulationSourceReportIdsFromGraph(
  nodes: WorkspaceNode[] | undefined,
  edges: WorkspaceEdge[] | undefined
): Record<string, string> {
  const next: Record<string, string> = {};
  const edgeList = edges ?? [];
  for (const node of nodes ?? []) {
    if (node.type !== "simulation" && node.type !== "combined_simulation") continue;
    const entityId = node.entityId.trim();
    if (!entityId) continue;
    const parentId = parentIdFor(edgeList, node.id);
    if (!parentId) continue;
    const parent = (nodes ?? []).find((entry) => entry.id === parentId);
    const reportEntityId = parent?.type === "report" ? parent.entityId.trim() : "";
    if (reportEntityId) next[entityId] = reportEntityId;
  }
  return next;
}

/**
 * Resolves each simulation node's matched report entity id from canvas parents only.
 * Does not GET /backtests/{id} on canvas open (avoids 429 storms).
 */
export function useWorkspaceSimulationSourceReportIds(
  nodes: WorkspaceNode[] | undefined,
  edges: WorkspaceEdge[] | undefined
): Record<string, string> {
  const graphSeed = useMemo(
    () => simulationSourceReportIdsFromGraph(nodes, edges),
    [nodes, edges]
  );
  /** Survives disconnect: once we know a sim↔report match, keep it for the session. */
  const knownRef = useRef<Map<string, string>>(new Map());
  const [known, setKnown] = useState<Record<string, string>>({});

  useEffect(() => {
    if (Object.keys(graphSeed).length === 0) return;
    let changed = false;
    for (const [entityId, reportId] of Object.entries(graphSeed)) {
      if (!reportId.trim()) continue;
      if (knownRef.current.get(entityId) === reportId) continue;
      knownRef.current.set(entityId, reportId);
      changed = true;
    }
    if (!changed) return;
    setKnown(Object.fromEntries(knownRef.current));
  }, [graphSeed]);

  return useMemo(() => {
    const merged: Record<string, string> = { ...known };
    for (const [entityId, reportId] of Object.entries(graphSeed)) {
      if (reportId) merged[entityId] = reportId;
    }
    for (const [entityId, reportId] of Object.entries(merged)) {
      if (!reportId.trim()) delete merged[entityId];
    }
    return merged;
  }, [known, graphSeed]);
}
